import { formatMoney as formatMoneyFromTemplate } from './money-formatting.js';

const STORAGE_KEY = 'augarten:wishlist:v1';

const getStoreRoot = () => window.Shopify?.routes?.root || '/';
const getStoreUrl = (path = '') => new URL(path, window.location.origin + getStoreRoot()).toString();

/** @type {Map<string, object>} */
const productJsonCache = new Map();

let stringsCache = null;
const getStrings = () => {
  if (stringsCache) return stringsCache;
  const el = document.getElementById('wishlist-strings');
  if (el instanceof HTMLScriptElement) {
    try {
      stringsCache = JSON.parse(el.textContent || '{}');
    } catch {
      stringsCache = {};
    }
  } else {
    stringsCache = {};
  }
  return stringsCache;
};

/** Matches Liquid `| money` using `shop.money_format` from wishlist-strings (Horizon-style). */
const formatMoney = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  const str = getStrings();
  const format = typeof str.money_format === 'string' ? str.money_format : '{{amount}}';
  const currency = str.money_currency || 'EUR';
  try {
    return formatMoneyFromTemplate(Math.round(n), format, currency);
  } catch {
    return (n / 100).toFixed(2);
  }
};

const loadItems = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    return data
      .map((row) => ({
        variantId: String(row?.variantId ?? ''),
        productId: String(row?.productId ?? ''),
        handle: String(row?.handle ?? '').trim(),
      }))
      .filter((row) => row.variantId && row.handle);
  } catch {
    return [];
  }
};

const saveItems = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
};

const dedupeByVariant = (items) => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (seen.has(item.variantId)) continue;
    seen.add(item.variantId);
    out.push(item);
  }
  return out;
};

const dispatchChange = (detail) => {
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail }));
  refreshAllToggles();
  updateHeaderCounts();
  void renderWishlistDrawer();
};

const wishlistHasVariant = (variantId) =>
  loadItems().some((item) => item.variantId === String(variantId));

const addItem = (entry) => {
  const items = loadItems();
  const next = dedupeByVariant([
    ...items.filter((i) => i.variantId !== String(entry.variantId)),
    {
      variantId: String(entry.variantId),
      productId: String(entry.productId ?? ''),
      handle: String(entry.handle).trim(),
    },
  ]);
  saveItems(next);
  dispatchChange({ action: 'add', variantId: String(entry.variantId) });
};

const removeItem = (variantId) => {
  const vid = String(variantId);
  const items = loadItems().filter((i) => i.variantId !== vid);
  saveItems(items);
  dispatchChange({ action: 'remove', variantId: vid });
};

const openOrFocusWishlistTab = (trigger = null) => {
  const drawer = document.querySelector('[data-drawer-id="cart-drawer"]');
  if (drawer instanceof HTMLElement && drawer.classList.contains('is-open')) {
    document.dispatchEvent(new CustomEvent('cart-drawer:select-tab', { detail: { index: 1 } }));
    return;
  }
  document.dispatchEvent(
    new CustomEvent('drawer:open-request', {
      detail: {
        id: 'cart-drawer',
        initialTab: 'wishlist',
        trigger: trigger instanceof HTMLElement ? trigger : null,
      },
    }),
  );
};

const toggleWishlistItem = (entry) => {
  const vid = String(entry.variantId);
  if (wishlistHasVariant(vid)) {
    removeItem(vid);
  } else {
    addItem(entry);
  }
};

const fetchProductJson = async (handle) => {
  if (productJsonCache.has(handle)) return productJsonCache.get(handle);
  const res = await fetch(getStoreUrl(`products/${encodeURIComponent(handle)}.js`));
  if (!res.ok) throw new Error(`Product ${handle}`);
  const json = await res.json();
  productJsonCache.set(handle, json);
  return json;
};

const findVariantInProduct = (product, variantId) => {
  const vid = String(variantId);
  const variants = product?.variants;
  if (!Array.isArray(variants)) return null;
  return variants.find((v) => String(v.id) === vid) || null;
};

const imageUrlFromVariant = (variant, product) => {
  const vfi = variant?.featured_image;
  if (vfi && typeof vfi === 'object' && vfi.src) return vfi.src;
  if (typeof vfi === 'string') return vfi;
  const pfi = product?.featured_image;
  if (pfi && typeof pfi === 'object' && pfi.src) return pfi.src;
  if (typeof pfi === 'string') return pfi;
  return '';
};

const refreshAllToggles = () => {
  const str = getStrings();
  const addLabel = str.add || 'Add to wishlist';
  const removeLabel = str.remove || 'Remove from wishlist';

  document.querySelectorAll('[data-wishlist-toggle]').forEach((el) => {
    if (!(el instanceof HTMLButtonElement)) return;
    const vid = el.dataset.variantId;
    if (!vid) return;
    const pressed = wishlistHasVariant(vid);
    el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    el.setAttribute('aria-label', pressed ? removeLabel : addLabel);
  });
};

/** Cart + Wishlist counts in the cart drawer tab strip (badges hidden when 0). */
const updateDrawerTabBadges = () => {
  const cartCountEl = document.querySelector('[data-cart-count]');
  let cartN = 0;
  if (cartCountEl instanceof HTMLElement && cartCountEl.textContent) {
    const parsed = Number.parseInt(cartCountEl.textContent.trim(), 10);
    if (Number.isFinite(parsed)) cartN = parsed;
  }
  const wishN = loadItems().length;

  document.querySelectorAll('[data-drawer-tab-badge="cart"]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = String(cartN);
    el.hidden = cartN === 0;
  });
  document.querySelectorAll('[data-drawer-tab-badge="wishlist"]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = String(wishN);
    el.hidden = wishN === 0;
  });
};

const updateHeaderCounts = () => {
  const n = loadItems().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = String(n);
    el.hidden = n === 0;
  });
  document.querySelectorAll('[data-wishlist-open-drawer]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.classList.toggle('wishlist-trigger--has-items', n > 0);
  });
  updateDrawerTabBadges();
};

let drawerRenderToken = 0;

const renderWishlistDrawer = async () => {
  const root = document.querySelector('[data-wishlist-drawer-root]');
  if (!root) return;

  const loading = root.querySelector('[data-wishlist-drawer-loading]');
  const emptyEl = root.querySelector('[data-wishlist-drawer-empty]');
  const listEl = root.querySelector('[data-wishlist-drawer-list]');
  if (!emptyEl || !listEl) return;

  const items = loadItems();
  const str = getStrings();

  if (items.length === 0) {
    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }
    listEl.innerHTML = '';
    listEl.hidden = true;
    emptyEl.hidden = false;
    return;
  }

  emptyEl.hidden = true;
  listEl.hidden = false;

  const token = ++drawerRenderToken;
  if (loading instanceof HTMLElement) loading.hidden = false;

  const rows = [];
  for (const item of items) {
    try {
      const product = await fetchProductJson(item.handle);
      if (token !== drawerRenderToken) return;
      const variant = findVariantInProduct(product, item.variantId);
      if (!variant) continue;

      const imgUrl = imageUrlFromVariant(variant, product);

      rows.push({
        item,
        product,
        variant,
        imgUrl,
        title: product.title,
        variantTitle: variant.title || '',
        price: variant.price,
        url: `${getStoreRoot()}products/${item.handle}?variant=${item.variantId}`,
      });
    } catch {
      rows.push({
        item,
        error: true,
        title: item.handle,
      });
    }
  }

  if (token !== drawerRenderToken) return;
  if (loading instanceof HTMLElement) loading.hidden = true;

  listEl.innerHTML = '';
  listEl.setAttribute('aria-label', str.drawer_list_label || 'Wishlist');

  for (const row of rows) {
    if (row.error) {
      const li = document.createElement('li');
      li.className = 'cart-drawer__item border-b border-stone-200 py-6 type-body-sm';
      li.setAttribute('role', 'listitem');
      li.textContent = str.error_product || 'Product unavailable.';
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className =
        'mt-2 type-body-sm inline-flex cursor-pointer border-0 bg-transparent p-0 underline underline-offset-3';
      rm.setAttribute('data-wishlist-drawer-remove', '');
      rm.dataset.variantId = row.item.variantId;
      rm.textContent = str.remove || 'Remove';
      li.appendChild(document.createElement('br'));
      li.appendChild(rm);
      listEl.appendChild(li);
      continue;
    }

    const li = document.createElement('li');
    li.className =
      'cart-drawer__item grid grid-cols-[7rem_1fr] gap-2 border-b border-stone-200 py-6';
    li.setAttribute('role', 'listitem');

    const imgWrap = document.createElement('a');
    imgWrap.href = row.url;
    imgWrap.className =
      'cart-drawer__item-image block w-27 shrink-0 aspect-square overflow-hidden bg-stone-200';
    if (row.imgUrl) {
      const src = row.imgUrl.startsWith('//') ? `https:${row.imgUrl}` : row.imgUrl;
      const img = document.createElement('img');
      img.src = src.includes('?') ? `${src}&width=280` : `${src}?width=280`;
      img.alt = row.title;
      img.className = 'block h-full w-full object-cover';
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }
    li.appendChild(imgWrap);

    const content = document.createElement('div');
    content.className = 'cart-drawer__item-content flex min-w-0 flex-col';

    const top = document.createElement('div');
    top.className = 'flex items-start justify-between gap-4 mt-[-2px]';

    const titleBlock = document.createElement('div');
    titleBlock.className = 'min-w-0';
    const titleA = document.createElement('a');
    titleA.href = row.url;
    titleA.className = 'cart-drawer__item-title type-body-md block leading-tight';
    titleA.textContent = row.title;
    titleBlock.appendChild(titleA);
    if (!row.product.has_only_default_variant && row.variantTitle) {
      const vt = document.createElement('p');
      vt.className = 'type-body-sm mt-1 text-stone-400';
      vt.textContent = row.variantTitle;
      titleBlock.appendChild(vt);
    }

    const price = document.createElement('p');
    price.className = 'cart-drawer__item-price type-body-md shrink-0';
    price.textContent = formatMoney(Number(row.variant.price));

    top.appendChild(titleBlock);
    top.appendChild(price);
    content.appendChild(top);

    const controls = document.createElement('div');
    controls.className = 'cart-drawer__item-controls flex flex-wrap items-center gap-x-4 gap-y-2 mt-3';

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'type-body-sm inline-flex cursor-pointer border-0 bg-transparent p-0 underline underline-offset-3';
    addBtn.setAttribute('data-wishlist-drawer-add', '');
    addBtn.dataset.variantId = row.item.variantId;
    addBtn.textContent = str.add_to_cart || 'Add to cart';

    const rmBtn = document.createElement('button');
    rmBtn.type = 'button';
    rmBtn.className = 'type-body-sm inline-flex cursor-pointer border-0 bg-transparent p-0 underline underline-offset-3';
    rmBtn.setAttribute('data-wishlist-drawer-remove', '');
    rmBtn.dataset.variantId = row.item.variantId;
    rmBtn.setAttribute(
      'aria-label',
      `${str.remove || 'Remove'} — ${row.title}`,
    );
    rmBtn.textContent = str.remove || 'Remove';

    controls.appendChild(addBtn);
    controls.appendChild(rmBtn);
    content.appendChild(controls);

    li.appendChild(content);
    listEl.appendChild(li);
  }
};

document.addEventListener('click', (event) => {
  const openWishlist = event.target.closest('[data-wishlist-open-drawer]');
  if (openWishlist instanceof HTMLElement) {
    event.preventDefault();
    openOrFocusWishlistTab(openWishlist);
    return;
  }

  const toggle = event.target.closest('[data-wishlist-toggle]');
  if (toggle instanceof HTMLButtonElement) {
    event.preventDefault();
    const vid = toggle.dataset.variantId;
    const handle = toggle.dataset.productHandle;
    const pid = toggle.dataset.productId;
    if (!vid || !handle) return;
    const wasInList = wishlistHasVariant(vid);
    toggleWishlistItem({ variantId: vid, productId: pid || '', handle });
    if (
      toggle.hasAttribute('data-wishlist-toggle-opens-drawer') &&
      !wasInList &&
      wishlistHasVariant(vid)
    ) {
      requestAnimationFrame(() => {
        openOrFocusWishlistTab();
      });
    }
    return;
  }

  const removeBtn = event.target.closest('[data-wishlist-drawer-remove]');
  if (removeBtn instanceof HTMLElement) {
    event.preventDefault();
    const vid = removeBtn.dataset.variantId;
    if (vid) removeItem(vid);
    return;
  }

  const addBtn = event.target.closest('[data-wishlist-drawer-add]');
  if (addBtn instanceof HTMLElement) {
    event.preventDefault();
    const vid = addBtn.dataset.variantId;
    if (!vid) return;
    addBtn.setAttribute('aria-disabled', 'true');
    const formData = new FormData();
    formData.append('id', String(vid));
    formData.append('quantity', '1');
    fetch(getStoreUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (!res.ok) {
          const errPayload = await res.json().catch(() => ({}));
          throw new Error(errPayload?.description || errPayload?.message || 'Add to cart failed');
        }
        document.dispatchEvent(new CustomEvent('cart:section-refresh-request'));
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        addBtn.removeAttribute('aria-disabled');
      });
  }
});

window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  refreshAllToggles();
  updateHeaderCounts();
  void renderWishlistDrawer();
});

document.addEventListener('wishlist:sync-ui', () => {
  refreshAllToggles();
});

document.addEventListener('cart:updated', () => {
  void renderWishlistDrawer();
  refreshAllToggles();
  updateDrawerTabBadges();
});

const initWishlist = () => {
  refreshAllToggles();
  updateHeaderCounts();
  void renderWishlistDrawer();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  initWishlist();
}
